import React, { useState } from 'react';
import AuthService from '../../API/AuthService';
import { IconCheck } from '../../components/icons';
import cl from './Modal.module.css';

/**
 * Редактирование реквизитов центра.
 *
 * По этим полям выставляются счета и закрывающие документы, поэтому ИНН, КПП
 * и форму организации проверяет ещё и сервер — форма лишь показывает ошибку
 * раньше, чем человек нажмёт «Сохранить».
 */
export default function DetailsModal({ centre, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: centre?.name || '',
        city: centre?.city || '',
        contact_person: centre?.contact_person || '',
        phone: centre?.phone || '',
        email: centre?.email || '',
        type: centre?.type === 'OOO' ? 'OOO' : 'IP',
        inn: centre?.inn || '',
        kpp: centre?.kpp || '',
        legal_address: centre?.legal_address || '',
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const [done, setDone] = useState(false);

    const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    function validate() {
        if (!form.name.trim()) return 'Укажите название центра';
        const inn = form.inn.trim();
        if (inn && !/^\d+$/.test(inn)) return 'ИНН должен состоять только из цифр';
        if (inn && inn.length !== 10 && inn.length !== 12) {
            return 'ИНН должен содержать 10 цифр (ООО) или 12 (ИП)';
        }
        const kpp = form.kpp.trim();
        if (kpp && !/^\d{9}$/.test(kpp)) return 'КПП должен содержать 9 цифр';
        return '';
    }

    async function submit(e) {
        e.preventDefault();
        const problem = validate();
        if (problem) {
            setErr(problem);
            return;
        }
        setErr('');
        setSaving(true);
        try {
            const saved = await AuthService.updateMyCentre(form);
            setDone(true);
            // Кабинет перечитывает центр сам — передаём сохранённое,
            // чтобы карточка обновилась без лишнего запроса.
            if (onSaved) onSaved(saved);
        } catch (e) {
            setErr(e?.response?.data?.message
                || 'Не удалось сохранить. Попробуйте ещё раз.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className={cl.backdrop} onClick={onClose}>
            <div className={cl.modal} onClick={e => e.stopPropagation()}>
                <button className={cl.close} onClick={onClose} aria-label="Закрыть">×</button>

                {done ? (
                    <div className={cl.done}>
                        <div className={cl.doneIcon}>
                            <IconCheck width={42} height={42} strokeWidth={3}/>
                        </div>
                        <h2 className={cl.title}>Реквизиты сохранены</h2>
                        <p className={cl.lead}>
                            Их увидит бухгалтерия при выставлении следующего счёта.
                        </p>
                        <button className={cl.btnPrimary} onClick={onClose}>Готово</button>
                    </div>
                ) : (
                    <form onSubmit={submit}>
                        <h2 className={cl.title}>Реквизиты центра</h2>
                        <p className={cl.lead}>
                            По ним выставляются счета и закрывающие документы.
                        </p>

                        {err && <div className={cl.err}>{err}</div>}

                        <div className={cl.field}>
                            <label htmlFor="d-name">Название центра</label>
                            <input id="d-name" type="text" required value={form.name}
                                onChange={set('name')} placeholder="Например: Робокор"/>
                        </div>
                        <div className={cl.field}>
                            <label htmlFor="d-city">Город</label>
                            <input id="d-city" type="text" value={form.city}
                                onChange={set('city')} placeholder="Москва"/>
                        </div>
                        <div className={cl.field}>
                            <label htmlFor="d-person">Контактное лицо</label>
                            <input id="d-person" type="text" value={form.contact_person}
                                onChange={set('contact_person')} placeholder="Имя и фамилия"/>
                        </div>
                        <div className={cl.field}>
                            <label htmlFor="d-phone">Телефон</label>
                            <input id="d-phone" type="tel" value={form.phone}
                                onChange={set('phone')} placeholder="+7 ___ ___-__-__"/>
                        </div>
                        <div className={cl.field}>
                            <label htmlFor="d-email">E-mail</label>
                            <input id="d-email" type="email" value={form.email}
                                onChange={set('email')} placeholder="buh@centre.ru"/>
                        </div>

                        <div className={cl.field}>
                            <label htmlFor="d-type">Форма организации</label>
                            <select id="d-type" value={form.type} onChange={set('type')}>
                                <option value="IP">ИП</option>
                                <option value="OOO">ООО</option>
                            </select>
                        </div>
                        <div className={cl.field}>
                            <label htmlFor="d-inn">ИНН</label>
                            <input id="d-inn" type="text" inputMode="numeric" value={form.inn}
                                onChange={set('inn')}
                                placeholder={form.type === 'OOO' ? '10 цифр' : '12 цифр'}/>
                        </div>
                        {form.type === 'OOO' && (
                            <div className={cl.field}>
                                <label htmlFor="d-kpp">КПП</label>
                                <input id="d-kpp" type="text" inputMode="numeric" value={form.kpp}
                                    onChange={set('kpp')} placeholder="9 цифр"/>
                            </div>
                        )}
                        <div className={cl.field}>
                            <label htmlFor="d-addr">Юридический адрес</label>
                            <textarea id="d-addr" rows={2} value={form.legal_address}
                                onChange={set('legal_address')}
                                placeholder="Индекс, город, улица, дом"/>
                        </div>

                        <button type="submit" className={cl.btnPrimary} disabled={saving}>
                            {saving ? 'Сохраняем…' : 'Сохранить'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
